'use client';

import { Box, Typography, Button } from '@mui/material';
import Link from 'next/link';
import LockIcon from '@mui/icons-material/Lock';
import { usePathname } from 'next/navigation';
import { useAuthStore } from '@/stores/authStore';
import { useConfigStore } from '@/stores/configStore';
import { log } from '@/lib/utils/logger';
import { AnonymousSignInGate } from '@/components/auth/AnonymousSignInGate';
import { ResourceUnavailableView } from '@/components/common/ResourceUnavailableView';
import {
  deepLinkUnavailableDetail,
  deepLinkUnavailableHeadline,
  parseDeepLinkResource,
  shouldPromptAnonymousSignIn,
  toRelativeAppPath,
} from '@/lib/auth/resourceDeepLink';

interface AccessErrorProps {
  error?: {
    status?: number;
    message?: string;
  };
}

export default function AccessErrorPage({ error }: AccessErrorProps) {
  const { currentUser } = useAuthStore();
  const { systemConfig } = useConfigStore();

  const pathnameHook = usePathname as unknown;
  let safePathname: string | null = null;
  if (typeof pathnameHook === 'function') {
    try {
      safePathname = (pathnameHook as () => string | null)();
    } catch (hookError) {
      log.debug('[AccessErrorPage] usePathname unavailable in this environment', hookError);
    }
  }

  const basePath = systemConfig?.basePath ?? '';
  const relativePath = safePathname ? toRelativeAppPath(safePathname, basePath) : '';
  const deepLink = relativePath ? parseDeepLinkResource(relativePath) : null;

  if (shouldPromptAnonymousSignIn(currentUser, error?.status)) {
    return <AnonymousSignInGate />;
  }

  if (deepLink && error && (error.status === 404 || error.status === 403)) {
    return (
      <ResourceUnavailableView
        headline={deepLinkUnavailableHeadline(deepLink.kind)}
        detail={deepLinkUnavailableDetail(deepLink.kind, deepLink.label)}
        searchPath={deepLink.searchPath}
        searchLabel={deepLink.searchLabel}
      />
    );
  }

  if (error?.status === 404) {
    return (
      <ResourceUnavailableView
        headline="This page could not be found"
        detail={error.message}
        searchPath="/"
        searchLabel="Return to Home"
      />
    );
  }

  const buttonHref = deepLink ? `${basePath}${deepLink.searchPath}` : `${basePath}/`;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 'calc(100vh - 64px)',
        gap: 2,
        p: 3,
      }}
    >
      <LockIcon color="error" sx={{ fontSize: 64 }} />
      <Typography variant="h4" component="h1" gutterBottom>
        Unauthorized Access
      </Typography>
      <Typography variant="body1" color="text.secondary" align="center" sx={{ maxWidth: 600 }}>
        {error?.message ??
          'You do not have the required permissions to access this page. Please contact your administrator if you believe this is an error.'}
      </Typography>

      <Link href={deepLink ? buttonHref : `${basePath}/`} passHref>
        <Button variant="contained" component="span" sx={{ mt: 2 }}>
          {deepLink?.searchLabel ?? 'Return to Home'}
        </Button>
      </Link>
    </Box>
  );
}
